import type { JSX } from 'preact';
import { T, Icon } from '../tokens';
import type { IconName } from '../tokens';
import type { TabId } from '../router';

export type NavVariant = 'tabs4' | 'tabs5' | 'fab';

interface NavItemDef {
  readonly id: TabId;
  readonly label: string;
  readonly icon: IconName;
}

const ITEMS_4: readonly NavItemDef[] = [
  { id: 'feed', label: 'Главный', icon: 'home' },
  { id: 'tree', label: 'Имущество', icon: 'tree' },
  { id: 'calendar', label: 'Календарь', icon: 'cal' },
  { id: 'history', label: 'Ассистент', icon: 'spark' },
];

const ITEMS_5: readonly NavItemDef[] = [
  { id: 'feed', label: 'Главный', icon: 'home' },
  { id: 'tree', label: 'Имущество', icon: 'tree' },
  { id: 'calendar', label: 'Календарь', icon: 'cal' },
  { id: 'notifs', label: 'Уведомления', icon: 'bell' },
  { id: 'history', label: 'Ассистент', icon: 'spark' },
];

const FAB_SIDE_L: readonly NavItemDef[] = [
  { id: 'feed', label: 'Главный', icon: 'home' },
  { id: 'tree', label: 'Имущество', icon: 'tree' },
];

const FAB_SIDE_R: readonly NavItemDef[] = [
  { id: 'calendar', label: 'Календарь', icon: 'cal' },
  { id: 'history', label: 'Ассистент', icon: 'spark' },
];

interface BottomNavProps {
  readonly tab: TabId;
  readonly onTabChange: (next: TabId) => void;
  readonly onOpenVoice: () => void;
  readonly variant?: NavVariant;
}

export function BottomNav({
  tab,
  onTabChange,
  onOpenVoice,
  variant = 'fab',
}: BottomNavProps): JSX.Element {
  if (variant === 'fab') {
    return (
      <div style={navContainerStyle}>
        {FAB_SIDE_L.map((i) => (
          <NavItem key={i.id} item={i} active={tab === i.id} onClick={() => onTabChange(i.id)} />
        ))}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={onOpenVoice}
            aria-label="Голосовой ввод"
            style={fabStyle}
          >
            <Icon name="mic" size={28} color="#fff" />
          </button>
        </div>
        {FAB_SIDE_R.map((i) => (
          <NavItem key={i.id} item={i} active={tab === i.id} onClick={() => onTabChange(i.id)} />
        ))}
      </div>
    );
  }

  const items = variant === 'tabs4' ? ITEMS_4 : ITEMS_5;
  return (
    <div style={{ ...navContainerStyle, padding: '6px 4px calc(12px + env(safe-area-inset-bottom))' }}>
      {items.map((i) => (
        <NavItem key={i.id} item={i} active={tab === i.id} onClick={() => onTabChange(i.id)} />
      ))}
    </div>
  );
}

const navContainerStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  padding: '6px 8px calc(12px + env(safe-area-inset-bottom))',
  background: 'rgba(244,241,236,0.92)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderTop: `1px solid ${T.border}`,
  gap: 0,
};

const fabStyle: JSX.CSSProperties = {
  width: 60,
  height: 60,
  borderRadius: 30,
  background: T.brand,
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 8px 20px rgba(79,123,94,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: -22,
};

interface NavItemProps {
  readonly item: NavItemDef;
  readonly active: boolean;
  readonly onClick: () => void;
}

function NavItem({ item, active, onClick }: NavItemProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: '8px 4px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: active ? T.ink : T.ink3,
        fontFamily: T.font,
      }}
    >
      <Icon name={item.icon} size={22} color={active ? T.ink : T.ink3} />
      <div style={{ fontSize: 10.5, fontWeight: active ? 700 : 600, letterSpacing: -0.1 }}>
        {item.label}
      </div>
    </button>
  );
}
